import React, { useEffect, useMemo, useState } from 'react'

import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { Field, FieldHookConfig, getIn, useField } from 'formik'

import { baseInputClasses } from './KeyInput'
import LoadingIcon from '@/components/LoadingIcon'
import { useGetSsnUnmaskedQuery } from '@/services/applicationProfile'
import { Profile } from '@/types/unlock'

interface CustomProps {
  name: string
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  disabled: boolean
  maskedFormat: string
  profile: Profile
  style?: string
  afterOnChange?: () => void
  showError?: boolean
  errors?: any
  touched?: any
  containerClasses?: string
}

// @TODO - Remove `any` by defining what props we should pass to <NumberFormat />
type CustomNumberFormatProps = any

type KeyMaskedSSNInputProps = CustomProps & CustomNumberFormatProps & FieldHookConfig<string>

/**
 * @deprecated key-* components are no longer supported.
 */
const KeyMaskedSSNInput = ({ name, profileUuid, ...props }: KeyMaskedSSNInputProps) => {
  const {
    setFieldValue,
    showError = false,
    afterOnChange,
    errors,
    touched,
    containerClasses,
    className: innerClasses,
    disabled = false,
  } = props

  const [value, setValue] = useState('')
  const [showValue, setShowValue] = useState(false)
  const [skipFetchUnmasked, setSkipFetchUnmasked] = useState(false)
  const { data: applicantSsnUnmasked, isLoading } = useGetSsnUnmaskedQuery(profileUuid, {
    skip: skipFetchUnmasked || !profileUuid,
  })

  useEffect(() => {
    if (applicantSsnUnmasked && applicantSsnUnmasked?.social_security_number) {
      setFieldValue(name, applicantSsnUnmasked.social_security_number.replaceAll('-', ''), true)
      setValue(applicantSsnUnmasked.social_security_number)
      setSkipFetchUnmasked(true)
      if (!applicantSsnUnmasked.social_security_number) setShowValue(true)
    }
  }, [applicantSsnUnmasked])

  const toggleShowValue = () => {
    if (!value) {
      setShowValue(true)
      return
    }
    setShowValue(!showValue)
  }

  const handleChange = (e) => {
    let inputValue = e.target.value
    const formattedValue = inputValue.replace(/\D/g, '').slice(0, 9)

    let maskedValue = ''
    let index = 0
    for (let i = 0; i < formattedValue.length; i++) {
      if (index === 3 || index === 5) {
        maskedValue += '-'
      }
      maskedValue += formattedValue[i]
      index++
    }

    setValue(maskedValue)
    inputValue = inputValue.replaceAll('-', '')
    inputValue = inputValue.replaceAll('_', '')
    if (inputValue.length <= 9) {
      setFieldValue(name, inputValue, true)
      if (afterOnChange) afterOnChange()
    }
  }

  const hiddenSSN = useMemo(() => {
    let hidden = ''
    for (let i = 0; i < value.length; i++) {
      if (i < 7 && value[i] !== '-') {
        hidden += '*'
      } else {
        hidden += value[i]
      }
    }
    return hidden
  }, [value, showValue])

  const displayEyeIcon = () => {
    if (!showValue) {
      return (
        <EyeIcon
          onClick={toggleShowValue}
          className='cursor-pointer'
          width={22}
          height={22}
        />
      )
    }
    return (
      <EyeSlashIcon
        onClick={toggleShowValue}
        className='cursor-pointer'
        width={22}
        height={22}
      />
    )
  }

  if (isLoading)
    return (
      <div className='w-full flex justify-center items-center'>
        <LoadingIcon />
      </div>
    )

  return (
    <div className={`${containerClasses}`}>
      <div className={`relative flex items-center`}>
        <Field name={name}>
          {({ field }) => (
            <input
              {...field}
              className={`w-full ${baseInputClasses} ${innerClasses}`}
              value={showValue ? value : hiddenSSN}
              placeholder={props.placeholder}
              onChange={
                showValue
                  ? (e) => handleChange(e)
                  : () => {
                      setShowValue(true)
                    }
              }
              disabled={disabled}
            />
          )}
        </Field>
        <div className='absolute right-2'>{displayEyeIcon()}</div>
      </div>
      {showError && !!getIn(errors, name) && !!getIn(touched, name) && (
        <div className='text-error'>{getIn(errors, name)}</div>
      )}
    </div>
  )
}

export default KeyMaskedSSNInput
